import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { ICryptoService } from '../../../services/crypto.service';
import { ICryptoStore } from '../../../stores/crypto.store';
import { IUserService } from '../../user/user.service';
import { ChangeWorkspaceOwnerModal } from '../components/ChangeWorkspaceOwnerModal';
import { DeleteWorkspaceConfirmationModal } from '../components/DeleteWorkspaceConfirmationModal';
import { MoreWorkspaceOptions } from '../components/MoreWorkspaceOptions';
import { RenameWorkspaceModal } from '../components/RenameWorkspaceModal';
import { ResetWorkspaceConfirmationModal } from '../components/ResetWorkspaceConfirmationModal';
import { UpdateWorkspaceTimeZoneModalContainer } from '../sub/UpdateWorkspaceTimeZone/containers/UpdateWorkspaceTimeZoneModal.container';
import { IWorkspaceService } from '../workspaces.service';
import { WorkspaceRole } from '../workspaces.store';

export const MoreOptionsWorkspaceContainer = observer((): ReactElement => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const workspaceService = useInjection(IWorkspaceService.$);
  const cryptoService = useInjection(ICryptoService.$);
  const userService = useInjection(IUserService.$);
  const cryptoStore = useInjection(ICryptoStore.$);

  const { isLoading, withLoaderAsync } = useLoader();

  const [renameWorkspaceName, setRenameWorkspaceName] = useState('');
  const [renameWorkspaceInputStatus, setRenameWorkspaceInputStatus] = useState(InputStatus.empty());
  const [showRenameWorkspaceModal, setShowRenameWorkspaceModal] = useState(false);
  const [deleteWorkspaceModalShown, setDeleteWorkspaceModalShown] = useState(false);
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  const [showResetWorkspaceModal, setShowResetWorkspaceModal] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerEmailStatus, setOwnerEmailStatus] = useState(InputStatus.empty());
  const [encryptionKeyUrl, setEncryptionKeyUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    void makeEncryptionKeyUrl();
    return (): void => {
      if (encryptionKeyUrl) {
        URL.revokeObjectURL(encryptionKeyUrl);
      }
    };
  }, [currentWorkspaceStore.currentWorkspace, cryptoStore.workspaceCryptos]);

  const makeEncryptionKeyUrl = async (): Promise<void> => {
    if (encryptionKeyUrl) {
      URL.revokeObjectURL(encryptionKeyUrl);
    }
    const url = await cryptoService.exportKey(currentWorkspaceStore.currentWorkspace.id);
    setEncryptionKeyUrl(url);
  };

  const toggleRenameWorkspaceModal = (): void => {
    if (isLoading) {
      return;
    }

    const workspace = currentWorkspaceStore.currentWorkspace;
    setShowRenameWorkspaceModal((prevState) => !prevState);
    setRenameWorkspaceName(workspace.name);
    setRenameWorkspaceInputStatus(InputStatus.empty());
  };

  const toggleResetWorkspaceModal = (): void => {
    if (isLoading) {
      return;
    }

    setShowResetWorkspaceModal((prevState) => !prevState);
  };

  const handleCancelReset = (): void => {
    if (isLoading) {
      return;
    }

    setShowResetWorkspaceModal(false);
  };

  const handleConfirmResetAsync = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      await workspaceService.resetCurrentWorkspaceAsync();
      setShowResetWorkspaceModal(false);
    }, 'reset-workspace-button');
  };

  const toggleChangeOwnerModal = (): void => {
    workspaceService.store.takeOverWorkspaceLimits = undefined;
    setShowChangeOwnerModal((prevState) => !prevState);
    setOwnerEmail('');
    setOwnerEmailStatus(InputStatus.empty());
  };

  const handleRenameWorkspaceNameChange = (value: string): void => {
    setRenameWorkspaceName(value);
    setRenameWorkspaceInputStatus(validateWorkspaceName(value));
  };

  const validateWorkspaceName = (value: string): InputStatus => {
    const errorCodes: TranslationKey[] = [];

    if (value.trim() === '') {
      errorCodes.push('field_cant_be_empty');
    }

    const isNotUnique = workspaceService.store.workspaces.map((w) => w.name.toLocaleUpperCase()).includes(value.trim().toLocaleUpperCase());

    if (isNotUnique) {
      errorCodes.push('DUPLICATE_WORKSPACE_NAME');
    }

    return InputStatus.buildFrom(errorCodes);
  };

  const renameWorkspace = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      if (renameWorkspaceInputStatus.isValid !== true) {
        return;
      }
      const workspace = currentWorkspaceStore.currentWorkspace;
      if (!workspace) {
        return;
      }
      const result = await workspaceService.renameWorkspaceAsync(workspace.id, renameWorkspaceName);

      if (result instanceof ACDLError) {
        setRenameWorkspaceInputStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? []));
      } else {
        setRenameWorkspaceInputStatus(InputStatus.empty());
        setShowRenameWorkspaceModal(false);
      }
    }, 'rename-workspace-button');
  };

  const handleChangeOwnerClicked = async (): Promise<void> => {
    await workspaceService.getCurrentWorkspaceMembersAsync();
    toggleChangeOwnerModal();
  };

  const handleChangeOwnerConfirmedAsync = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      if (ownerEmailStatus.isValid) {
        const result = await workspaceService.changeOwnerAsync(ownerEmail);

        if (result instanceof ACDLError && result.apiErrorResponse?.errorCodes?.includes('CAN_NOT_TAKEOVER_WORKSPACE')) {
          workspaceService.store.takeOverWorkspaceLimits = undefined;
          await workspaceService.getTakeOverWorkspaceLimitsAsync(ownerEmail);
        } else {
          toggleChangeOwnerModal();
          await userService.getUserInfoAsync();
        }
      }
    }, 'change-workspace-owner-button');
  };

  const handleDeleteClicked = (): void => {
    setDeleteWorkspaceModalShown(true);
  };

  const handleCancelDelete = (): void => {
    if (isLoading) {
      return;
    }

    setDeleteWorkspaceModalShown(false);
  };

  const handleConfirmDeleteAsync = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      await workspaceService.deleteCurrentWorkspaceAsync();
      setDeleteWorkspaceModalShown(false);
    }, 'delete-workspace-button');
  };

  const handleOwnerEmailChange = async (email: string): Promise<void> => {
    workspaceService.store.takeOverWorkspaceLimits = undefined;
    setOwnerEmail(email);
    setOwnerEmailStatus(InputStatus.valid());
    await workspaceService.getTakeOverWorkspaceLimitsAsync(email);
  };

  const { currentWorkspace } = currentWorkspaceStore;
  const showMoreOptions = currentWorkspace && currentWorkspace.role === WorkspaceRole.Owner;
  const workspaceTeam = workspaceService.store.currentWorkspaceMembers.filter((x) => x.role !== WorkspaceRole.Owner);

  return showMoreOptions ? (
    <>
      <MoreWorkspaceOptions
        onRenameClicked={toggleRenameWorkspaceModal}
        onChangeOwnerClicked={handleChangeOwnerClicked}
        onResetClicked={toggleResetWorkspaceModal}
        isEncrypted={currentWorkspace.encryption.encrypted ?? false}
        workspaceName={currentWorkspace.name ?? ''}
        onDeleteClicked={handleDeleteClicked}
        encryptionKeyUrl={encryptionKeyUrl ?? ''}
      />
      <RenameWorkspaceModal
        showRenameWorkspace={showRenameWorkspaceModal}
        renameWorkspaceName={renameWorkspaceName}
        renameWorkspaceNameStatus={renameWorkspaceInputStatus}
        toggleRenameWorkspace={toggleRenameWorkspaceModal}
        onNameChange={handleRenameWorkspaceNameChange}
        onRenameSave={renameWorkspace}
      />
      <DeleteWorkspaceConfirmationModal
        show={deleteWorkspaceModalShown}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDeleteAsync}
      />
      <ChangeWorkspaceOwnerModal
        showChangeOwner={showChangeOwnerModal}
        ownerEmail={ownerEmail}
        ownerEmailStatus={ownerEmailStatus}
        users={workspaceTeam}
        takeoverWorkspaceLimits={workspaceService.store.takeOverWorkspaceLimits}
        onOwnerEmailChange={handleOwnerEmailChange}
        toggleOwnerModal={toggleChangeOwnerModal}
        onOwnerSave={handleChangeOwnerConfirmedAsync}
      />
      <ResetWorkspaceConfirmationModal
        show={showResetWorkspaceModal}
        onCancel={handleCancelReset}
        onConfirm={handleConfirmResetAsync}
      />
      <UpdateWorkspaceTimeZoneModalContainer />
    </>
  ) : (
    <></>
  );
});
